/**
 * React bindings: useTranslation() (same shape as react-i18next's) and
 * I18nProvider, which loads the signed-in person's own locale (never the
 * workspace's, for the language) and keeps <html lang dir> in step with it.
 */
import {
  Fragment,
  useEffect,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { sessionIdentity, useAuthStore } from "@/lib/auth/store";
import { applyDocumentLocale, i18n, type I18nSnapshot } from "./core";
import { syncPersonLocale } from "./sync";

function useSnapshot(): I18nSnapshot {
  return useSyncExternalStore(
    i18n.subscribe,
    i18n.getSnapshot,
    i18n.getSnapshot,
  );
}

/**
 * `const { t } = useTranslation();` — works with or without I18nProvider
 * (screens rendered alone in tests read the same instance).
 */
export function useTranslation() {
  const s = useSnapshot();
  return {
    t: s.t,
    i18n: {
      language: s.language,
      resolvedLanguage: s.language,
      locale: s.locale,
      dir: () => s.dir,
      exists: i18n.exists,
      changeLanguage: i18n.changeLanguage,
    },
  };
}

/**
 * The person's own saved locale (GET /v1/users/me → personalLocale), or null
 * when they have none (the interface is then English or the device's
 * choice): what Profile shows as chosen, rather than the workspace's locale
 * that `locale` falls back to.
 */
export function usePersonLocale(): string | null {
  return useSnapshot().personLocale;
}

/**
 * Keeps the interface's locale in step with the signed-in person
 * (syncPersonLocale in ./sync.ts, which reads what they chose from
 * GET /v1/users/me). Fetched in an effect, never during render, so rendering
 * the providers creates no query (test/session-handling.test.tsx counts the
 * cache).
 *
 * Keyed on the session, not the token string: a refresh every 15 minutes
 * keeps the session and must not reload /users/me (S2-05).
 */
function LocaleSync() {
  const queryClient = useQueryClient();
  const session = useAuthStore(sessionIdentity);
  const tenant = useAuthStore((s) => s.activeTenantCode);

  useEffect(() => {
    if (!session || !tenant) {
      i18n.setUserPreferences(null);
      return;
    }
    return syncPersonLocale(queryClient);
  }, [session, tenant, queryClient]);

  return null;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const s = useSnapshot();
  useEffect(() => applyDocumentLocale(s), [s]);
  // Formatters (lib/money.ts) read the locale when called, not through a
  // hook, so the 193 call sites need no edit. Remounting the tree under a new
  // key when the locale or zone changes re-renders every one of them; it
  // happens on sign-in and on saving the profile, not while working.
  return (
    <>
      <LocaleSync />
      <Fragment key={`${s.locale}|${s.timeZone ?? ""}`}>{children}</Fragment>
    </>
  );
}
